import * as THREE from "./terra-vendor-three.module.min.js";

const roots = document.querySelectorAll("[data-terra-world]");

function textureUrl(root, name) {
  const resolution = name === "albedo" ? "2048" : "1024";
  return root.getAttribute(`data-earth-${name}-${resolution}`);
}

function loadTexture(loader, url, anisotropy, colorTexture = false) {
  return new Promise((resolve, reject) => {
    loader.load(url, (texture) => {
      texture.generateMipmaps = true;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.anisotropy = anisotropy;
      if (colorTexture) texture.colorSpace = THREE.SRGBColorSpace;
      resolve(texture);
    }, undefined, reject);
  });
}

async function createEarth(root) {
  const canvas = root.querySelector("[data-world-canvas]");
  const visual = root.querySelector("[data-world-visual]");
  if (!canvas || !visual || !window.WebGLRenderingContext) throw new Error("WebGL unavailable");

  const compact = window.matchMedia("(max-width: 900px)").matches || Number(navigator.deviceMemory || 4) <= 4;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
  const textureAnisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
  const loader = new THREE.TextureLoader();
  const [albedo, clouds, bump] = await Promise.all([
    loadTexture(loader, textureUrl(root, "albedo"), textureAnisotropy, true),
    loadTexture(loader, textureUrl(root, "clouds"), textureAnisotropy),
    compact ? Promise.resolve(null) : loadTexture(loader, textureUrl(root, "bump"), textureAnisotropy)
  ]);

  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.08;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, .1, 30);
  camera.position.set(0, 0, compact ? 7.05 : 7.35);

  const planet = new THREE.Group();
  planet.rotation.set(.08, -1.68, -.08);
  planet.scale.setScalar(1);
  scene.add(planet);

  const sphereMaterial = new THREE.MeshStandardMaterial({
    map: albedo,
    bumpMap: bump || null,
    bumpScale: bump ? .065 : 0,
    roughness: .84,
    metalness: .015
  });
  const sphere = new THREE.Mesh(new THREE.SphereGeometry(1.78, compact ? 44 : 72, compact ? 44 : 72), sphereMaterial);
  planet.add(sphere);

  const cloudLayer = new THREE.Mesh(
    new THREE.SphereGeometry(1.81, compact ? 36 : 52, compact ? 36 : 52),
    new THREE.MeshStandardMaterial({ color: 0xffffff, alphaMap: clouds, transparent: true, opacity: .44, depthWrite: false, roughness: 1 })
  );
  planet.add(cloudLayer);

  scene.add(new THREE.HemisphereLight(0xdfffe9, 0x06160f, 1.45));
  const sun = new THREE.DirectionalLight(0xfff1d0, 4.55);
  sun.position.set(-3.4, 3.2, 5.4);
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0x4ab67c, 1.35);
  fill.position.set(4, -1, 1.2);
  scene.add(fill);

  let frame = 0;
  let visible = true;
  let progress = 0;
  let pointerX = 0;
  let pointerY = 0;
  let lastMotion = performance.now();
  let lastPaint = 0;
  const frameInterval = compact ? 42 : 33;
  const qaStatic = new URLSearchParams(window.location.search).get("terra-qa") === "static";
  let renderWidth = 0;
  let renderHeight = 0;
  let renderDpr = 0;
  let disposed = false;

  function resize() {
    // Layout pixels only: the scroll transform must never resize the WebGL buffer.
    const width = visual.clientWidth;
    const height = visual.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (disposed || !width || !height || (width === renderWidth && height === renderHeight && dpr === renderDpr)) return;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    if (dpr !== renderDpr) renderer.setPixelRatio(dpr);
    renderer.setSize(width, height, false);
    renderWidth = width;
    renderHeight = height;
    renderDpr = dpr;
    renderer.render(scene, camera);
  }

  function schedule() {
    if (!disposed && !frame && visible && !document.hidden) frame = requestAnimationFrame(render);
  }

  function render(now) {
    frame = 0;
    if (disposed || !visible || document.hidden) return;
    if (now - lastPaint < frameInterval) {
      schedule();
      return;
    }

    const delta = Math.min(48, now - lastMotion);
    lastMotion = now;
    lastPaint = now;
    if (!reduced) {
      sphere.rotation.y += delta * .000047 * (1 - progress * .38);
      cloudLayer.rotation.y += delta * .000064;
      planet.rotation.y += (pointerX * .085 - planet.rotation.y - .18 + progress * .54) * .035;
      planet.rotation.x += (pointerY * .05 - planet.rotation.x + .1 - progress * .04) * .035;
    }
    renderer.render(scene, camera);
    if (!qaStatic && !reduced) schedule();
  }

  function onPointer(event) {
    if (compact || root.dataset.parallax === "false") return;
    pointerX = (event.clientX / window.innerWidth - .5) * 2;
    pointerY = (event.clientY / window.innerHeight - .5) * 2;
  }

  function onProgress(event) {
    progress = Math.max(0, Math.min(1, event.detail.progress));
    if (reduced) {
      renderer.render(scene, camera);
    }
  }

  function onVisibilityChange() {
    if (document.hidden) {
      cancelAnimationFrame(frame);
      frame = 0;
    } else {
      lastMotion = performance.now();
      schedule();
    }
  }

  const observer = new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting;
    if (visible) {
      lastMotion = performance.now();
      schedule();
    } else {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  }, { rootMargin: "12% 0px" });

  const resizeObserver = new ResizeObserver(resize);
  observer.observe(root);
  resizeObserver.observe(visual);
  if (!compact) window.addEventListener("pointermove", onPointer, { passive: true });
  document.addEventListener("visibilitychange", onVisibilityChange);
  root.addEventListener("terra:world-progress", onProgress);
  resize();
  schedule();

  return function dispose() {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(frame);
    observer.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener("pointermove", onPointer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    root.removeEventListener("terra:world-progress", onProgress);
    scene.traverse((object) => {
      if (object.geometry) object.geometry.dispose();
      if (object.material) object.material.dispose();
    });
    [albedo, clouds, bump].filter(Boolean).forEach((texture) => texture.dispose());
    renderer.dispose();
  };
}

roots.forEach(async (root) => {
  try {
    root.terraWorldDispose = await createEarth(root);
    root.classList.add("is-world-ready");
    const status = root.querySelector("[data-world-status]");
    if (status) status.textContent = "Pianeta T.E.R.R.A. 3D caricato. Esplora i collegamenti della rete.";
  } catch (error) {
    root.classList.add("is-world-fallback");
    root.dataset.worldError = error && error.message ? error.message : String(error || "Errore 3D");
  }
});

document.addEventListener("shopify:section:unload", (event) => {
  const root = event.target.querySelector && event.target.querySelector("[data-terra-world]");
  if (root && root.terraWorldDispose) root.terraWorldDispose();
});
